import { mutate } from 'swr';
import { cache } from 'swr/_internal';

import { FetcherParams } from '@/data/fetcher';

// SWR uses an internal cache key ($inf$) to store pages within a single SWR hook
// we can invalidate these pages by prop-drilling from useSWRInfinite and/or importing
// the hook even if we don't actually use data from fetcher (unlike react-query,
// swr will always call the fetcher afaict). As an alternative, we call mutate directly
// with the prefixed internal key.
//
// NOTE: if useSWRInfinite is not invoked with { revalidateAll: true },
//       then only the first page is invalidated
//
// See:
// - https://github.com/vercel/swr/issues/1670
// - https://github.com/vercel/swr/blob/ef63a30cf69f286f9b2a9f1109674a0fca541ab5/_internal/src/utils/mutate.ts#L80
export function mutateWithInfinite(
  callback: (params: Pick<FetcherParams, 'url'>) => boolean
) {
  for (const key of cache.keys()) {
    if (key.startsWith('$inf$')) {
      let search = key.slice('$inf$'.length);
      if (search.startsWith('#')) search = search.slice(1);

      let url = search
        .split(',')
        .find((i) => i.split(':')[0] === 'url')
        ?.split(':')?.[1];

      if (url?.startsWith('"')) url = url.slice(1);
      if (url?.endsWith('"')) url = url.slice(0, -1);
      if (url == null) continue;

      if (callback({ url })) {
        mutate(key);
      }
    }
  }

  mutate((key: FetcherParams) => {
    const url = key?.url;
    if (!url) return false;
    return callback({ url });
  });
}

/**
 * Similar to mutateWithInfinite but strips the baseURL from the cached URL before comparison.
 * This is useful for cases where the URL might be stored with or without the baseURL prefix.
 */
export function mutateWithInfiniteRelative(
  callback: (params: Pick<FetcherParams, 'url'>) => boolean
) {
  for (const key of cache.keys()) {
    if (key.startsWith('$inf$')) {
      let search = key.slice('$inf$'.length);
      if (search.startsWith('#')) search = search.slice(1);

      const urlMatch = search.match(/url:"([^"]+)"/);
      if (!urlMatch) continue;

      const url = urlMatch[1]; // This gets the URL without the quotes
      if (!url) continue;

      // Strip any base URL prefix (http://, https://, or specific domain)
      const relativeUrl = url.replace(/^(?:https?:\/\/[^/]+)?/, '');

      if (callback({ url: relativeUrl })) {
        mutate(key);
      }
    }
  }

  mutate((key: FetcherParams) => {
    const url = key?.url;
    if (!url) return false;
    const relativeUrl = url.replace(/^(?:https?:\/\/[^/]+)?/, '');
    return callback({ url: relativeUrl });
  });
}
